import { useState, useEffect, useCallback } from 'react';
import { api, uploadFile } from '../api/client';
import { cacheMediaUrls } from '../api/mediaCache';
import type { MediaItem } from '../types';

export function useMedia(siteId: string | undefined) {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchMedia = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.listMedia(siteId);
      // Cache presigned URLs for use by ImageUploader and other components
      cacheMediaUrls(data.map((item) => ({ key: item.key, url: item.url })));
      setMedia(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const upload = useCallback(
    async (file: File) => {
      if (!siteId) throw new Error('Missing siteId');
      setUploading(true);
      setUploadProgress(0);
      try {
        const result = await uploadFile(siteId, file, setUploadProgress);
        // Refresh media list to include the new file
        await fetchMedia();
        return result;
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    },
    [siteId, fetchMedia],
  );

  const remove = useCallback(
    async (mediaId: string) => {
      if (!siteId) throw new Error('Missing siteId');
      await api.deleteMedia(siteId, mediaId);
      setMedia((prev) => prev.filter((m) => m.mediaId !== mediaId));
    },
    [siteId],
  );

  return {
    media,
    loading,
    error,
    uploading,
    uploadProgress,
    refresh: fetchMedia,
    upload,
    remove,
  };
}
